import { createSelector } from '@reduxjs/toolkit';
import { getTranslateFunction } from '../api/util/getTranslateFunction';
import { RootState } from '../../store.model';
import CUTranslationsAPI from '../api/CUTranslationAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  TranslationCommanderObject,
  TranslationControlUnitObject,
} from '../api/CUTranslation.model';
import ControlUnitAPI from '../../controlUnit/api/ControlUnitAPI';
import _ from 'lodash';
import { orComparator } from '../../../util/lodash/orComparator';

export const getCUsTranslationsSelector = createSelector(
  [(_: RootState) => _],

  (state: RootState) => {
    return (
      keyTranslation: string,
      deviceId: string | undefined = '',
      controlUnitId: string | number = '',
      // Always would search in CommanderTranslations first, then in the type translation
      type: 'ram' | 'parameters' = 'ram',
      keyTranslationObject:
        | keyof TranslationCommanderObject
        | keyof TranslationControlUnitObject = 'shortText',
      // return keyTranslation if not found
      fallback = true,
      recursion = false
    ) => {
      const controlUnitTranslationInfo =
        CUTranslationsAPI.endpoints.getCUTranslationInfo.select({
          deviceId: deviceId,
          controlUnitId: controlUnitId!,
        })(state).data;
      const controlUnitTranslationData =
        CUTranslationsAPI.endpoints.downloadTranslationFile.select(
          controlUnitTranslationInfo?.file ?? skipToken
        )(state).data;

      const commanderTranslationInfo =
        CUTranslationsAPI.endpoints.getCommanderTranslationInfo.select(
          undefined
        )(state).data;
      const commanderTranslationData =
        CUTranslationsAPI.endpoints.downloadTranslationFile.select(
          commanderTranslationInfo ?? skipToken
        )(state).data;
      return getTranslateFunction(
        controlUnitTranslationData,
        commanderTranslationData,
        controlUnitId
      )(
        keyTranslation,
        controlUnitId,
        type,
        keyTranslationObject,
        fallback,
        recursion
      );
    };
  }
);

export const getCUTranslationParametersFromControlUnit = (
  state: RootState,
  props: { deviceId: string; controlUnitId: string }
) => {
  const { deviceId, controlUnitId } = props;
  const controlUnitTranslationInfo =
    CUTranslationsAPI.endpoints.getCUTranslationInfo.select({
      deviceId: deviceId,
      controlUnitId: controlUnitId!,
    })(state).data;
  const controlUnitTranslationData =
    CUTranslationsAPI.endpoints.downloadTranslationFile.select(
      controlUnitTranslationInfo?.file ?? skipToken
    )(state).data;
  return controlUnitTranslationData?.parameters;
};

export const getCUTranslationsResponses = (
  state: RootState,
  props: { argsDevices: { deviceId: string; controlUnitId: string }[] }
) => {
  const { argsDevices } = props;
  return argsDevices
    .map((arg) => {
      const {
        data: dataControlUnitTranslationInfoResponse,
        ...restControlUnitTranslationInfoResponse
      } = CUTranslationsAPI.endpoints.getCUTranslationInfo.select({
        deviceId: arg.deviceId,
        controlUnitId: arg.controlUnitId,
      })(state);
      const {
        data: controlUnitTranslationData,
        ...restControlUnitTranslationFileResponse
      } = CUTranslationsAPI.endpoints.downloadTranslationFile.select(
        dataControlUnitTranslationInfoResponse?.file ?? skipToken
      )(state);
      return _.chain({
        ...restControlUnitTranslationInfoResponse,
        data: controlUnitTranslationData,
      })
        .mergeWith(restControlUnitTranslationFileResponse, orComparator)
        .value();
    })
    .reduce(
      (acc, curr) => ({
        ..._.merge({ ...acc }, curr),
        data: [...acc.data, curr.data],
      }),
      { data: [] } as any
    );
};

export const getLoadingCUTranslationFromDevice = (
  state: RootState,
  props: { deviceId: string }
) => {
  const { deviceId } = props;
  const ControlUnitsResponse =
    ControlUnitAPI.endpoints.getControlUnitById.select(deviceId)(state);
  const loadingControlUnits = !!ControlUnitsResponse.isLoading;
  if (loadingControlUnits) return true;

  const argsDevicesMemo =
    ControlUnitsResponse.data?.ids.map((dcu) => ({
      deviceId,
      controlUnitId: dcu as string,
    })) ?? [];
  const { isLoading } = getCUTranslationsResponses(state, {
    argsDevices: argsDevicesMemo,
  });
  return isLoading;
};
